import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { LazyMotion, motion } from 'framer-motion'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { CalendarDots } from '@phosphor-icons/react/dist/ssr/CalendarDots'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { Compass } from '@phosphor-icons/react/dist/ssr/Compass'
import { FileDoc } from '@phosphor-icons/react/dist/ssr/FileDoc'
import { FileText } from '@phosphor-icons/react/dist/ssr/FileText'
import { FlagBanner } from '@phosphor-icons/react/dist/ssr/FlagBanner'
import { Handshake } from '@phosphor-icons/react/dist/ssr/Handshake'
import { Question } from '@phosphor-icons/react/dist/ssr/Question'
import { ShieldCheck } from '@phosphor-icons/react/dist/ssr/ShieldCheck'
import { ShareNetwork } from '@phosphor-icons/react/dist/ssr/ShareNetwork'
import { Timer } from '@phosphor-icons/react/dist/ssr/Timer'
import promptfreePoster from '@/assets/moss-orb-terrarium-poster.avif'
import finchtradeLogo from '@/assets/finchtrade-logo.avif'
import goshaPortrait from '@/assets/portrait-gosha-front-80.avif'
import lexPortrait from '@/assets/lex-portrait.avif'
import rocketIcon from '@/assets/rocket.avif'
import wavingHand from '@/assets/waving-hand.avif'
import wildflowerFieldPoster from '@/assets/wildflower-field-poster.avif'
import { EditorialQuote } from '@/components/article/EditorialQuote'
import { CaseStudyFaqAccordion } from '@/components/case-study/CaseStudyFaqAccordion'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import { SeoHead } from '@/components/seo/SeoHead'
import { siteConfig } from '@/config/site'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import { copyContactEmail } from '@/lib/copyContactEmail'

const pathname = '/hello/lex-842917'
const promptfreeVideoUrl = 'https://assets.gosha.ee/bgs/moss-orb-terrarium_web.mp4'
const offerVideoUrl = 'https://assets.gosha.ee/bgs/wildflower-field_web.mp4'
const loadDomAnimation = () =>
  import('@/lib/motion-dom-animation').then((mod) => mod.domAnimation)
const HomeTestimonials = lazy(() =>
  import('@/components/home/HomeTestimonials').then((mod) => ({ default: mod.HomeTestimonials }))
)

const readAlsoSlugs = [
  'automated-outbound-outreach-workflow',
  'personalized-outreach-visuals-workflow',
  'local-first-case-study-publishing-workflow',
  'clip-flip-linkedin-visual-workflow',
]

const caseStudySlug = (cs: ReturnType<typeof getAllCaseStudies>[number]) =>
  cs.page.slug

const benefits = [
  {
    title: 'Makes the offer feel calculated',
    body: 'The buyer sees their current setup, projected cost, proposed setup, and the specific savings logic before the asset asks for a next step.',
    Icon: Timer,
  },
  {
    title: 'Useful after the sales call',
    body: 'The call creates the context. The asset preserves it: money flow, compliance points, questions, proof, and next steps in one place.',
    Icon: ShareNetwork,
  },
  {
    title: 'Built for guarded buyers',
    body: 'PSPs, MSBs, EMIs, and high-risk operators do not always share everything upfront. The asset reflects only what was confirmed or safely inferred.',
    Icon: FileText,
  },
  {
    title: 'Easy to test',
    body: 'Start with one anonymized transcript. Create one internal sales asset. Judge it by whether it helps you ask better questions and shape the next offer.',
    Icon: ShieldCheck,
  },
]

const pilotSteps: ReactNode[] = [
  <>
    Pick <strong className="font-medium text-white">one real or anonymized FinchTrade sales call</strong>{' '}
    where the buyer gave enough context to calculate a stronger offer.
  </>,
  <>
    Gosha will turn it into <strong className="font-medium text-white">one internal sales asset</strong>:
    the buyer context, savings logic, objections, and offer angle.
  </>,
  <>
    Judge the test by whether <strong className="font-medium text-white">you would actually use it</strong>{' '}
    to sell: clearer questions, stronger offer, better next-call structure.
  </>,
]

const faqItems = [
  {
    question: 'Where does this fit in the FinchTrade sales flow?',
    answer:
      'After a qualified discovery call, before the next conversation or offer. The workflow turns what the buyer said into a working sales brief: current setup, cost logic, proposed FinchTrade setup, onboarding path, open questions, and the next move.',
  },
  {
    question: 'What information should the workflow pull out?',
    answer:
      'Payment rails, liquidity providers, current fees, trading volume, treasury setup, KYB or AML friction, decision blockers, requested features, and the buyer’s real reason for asking about price.',
  },
  {
    question: 'How does it support the savings argument?',
    answer:
      'It organizes the buyer’s current cost structure and compares it with the proposed FinchTrade setup. That gives you a clearer way to show monthly or annual savings instead of dropping a price without context.',
  },
  {
    question: 'Can this become the calculator idea from the call?',
    answer:
      'Yes. The same logic can power a calculator-style internal view: inputs from the call, current setup score, cost gap, and the strongest angle for the next conversation.',
  },
  {
    question: 'What if the buyer does not share enough?',
    answer:
      'It should mark gaps explicitly. If the buyer does not disclose a provider, fee, volume, or onboarding blocker, the asset turns that into a precise follow-up question instead of pretending the data exists.',
  },
  {
    question: 'How does this help with KYB, AML, and onboarding friction?',
    answer:
      'It can separate commercial upside from onboarding risk: what the buyer wants, what compliance still needs, where the account setup may slow down, and how to explain the path without making unsupported promises.',
  },
  {
    question: 'Who builds the first version?',
    answer:
      'Gosha creates the first version from the transcript, then turns the repeatable pieces into a section kit and workflow. After that, sales can use the workflow on their own.',
  },
  {
    question: 'How does the setup fee plus retainer model fit?',
    answer:
      'Setup builds the section kit, extraction rules, and workflow around FinchTrade’s sales process. Retainer covers support, new sections, prompt tuning, and changes as the sales process evolves.',
  },
  {
    question: 'What is the smallest useful starting point?',
    answer:
      'One anonymized transcript from a real qualified call. If the output gives you sharper questions, a stronger savings case, and a better next-call plan, the workflow is worth expanding.',
  },
  {
    question: 'Does Gosha work alone?',
    answer:
      'Mostly, yes. I handle strategy, design, engineering, copy structure, and the automation setup myself. If a project needs extra manual production, I bring in trusted help for that specific part.',
  },
]

const sectionTitleClass =
  "font-['Familjen_Grotesk'] text-[22px] font-normal leading-[1.12] tracking-[-0.2px] text-[var(--foreground)]"

const darkPrimaryCtaClass =
  'inline-flex w-fit shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-white px-4 py-2 text-[15px] font-medium tracking-[-0.15px] text-black [transition-property:background-color,transform] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:bg-white/92 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black'

const offerInlineIconClass = 'ml-1 inline-block size-4 -translate-y-px text-white/45'

const SectionHeader = ({ eyebrow, title }: { eyebrow?: string; title: ReactNode }) => (
  <div className="space-y-1">
    {eyebrow ? (
      <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-[var(--foreground-subtle)]">
        {eyebrow}
      </div>
    ) : null}
    <h2 className={sectionTitleClass}>{title}</h2>
  </div>
)

const Section = ({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string
  title: ReactNode
  children: ReactNode
}) => (
  <section className="space-y-4">
    <SectionHeader eyebrow={eyebrow} title={title} />
    {children}
  </section>
)

const FollowUpPromptPromo = ({
  onCopyEmail,
  emailCopied,
}: {
  onCopyEmail: () => void
  emailCopied: boolean
}) => (
  <section aria-label="Personalized follow-up asset example">
    <div className="relative min-h-[133px] overflow-hidden rounded-[16px] bg-black px-4 py-3 text-white">
      <div className="pointer-events-none absolute top-0 left-[240px] h-full w-[239px] overflow-hidden">
        <img
          src={promptfreePoster}
          alt=""
          width={239}
          height={134}
          className="h-full w-full max-w-none scale-[1.35] object-cover md:hidden"
          decoding="async"
        />
        <video
          className="hidden h-full w-full max-w-none scale-[1.35] object-cover md:block"
          src={promptfreeVideoUrl}
          poster={promptfreePoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        />
        <div className="absolute inset-y-0 left-0 w-[131px] bg-gradient-to-r from-black to-transparent" />
      </div>

      <div className="relative z-10 flex max-w-[314px] flex-col items-start gap-[18px]">
        <p className="font-['Familjen_Grotesk'] text-[22px] font-normal leading-[23.76px] tracking-[-0.44px] text-white [text-wrap:pretty]">
          One call transcript becomes a buyer-specific asset
        </p>
        <p className="flex flex-nowrap items-center gap-x-2 text-[15px] leading-[22.5px] tracking-[-0.16px]">
          <a
            href={siteConfig.calendlyUrl}
            rel="noreferrer"
            target="_blank"
            className={darkPrimaryCtaClass}
            aria-label="Schedule a call (opens in new tab)"
          >
            Schedule a call
          </a>
          <span className="inline-flex items-center gap-x-2 whitespace-nowrap">
            <span className="font-medium text-white/55">or</span>
            <button
              type="button"
              onClick={onCopyEmail}
              className="group inline-flex items-center gap-1 font-medium text-white underline decoration-white/45 underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              aria-label={emailCopied ? 'Email copied' : 'Copy email address'}
            >
              copy email
              <span className="relative inline-flex size-5 items-center justify-center">
                <motion.span
                  aria-hidden
                  animate={{
                    opacity: emailCopied ? 0 : 1,
                    scale: emailCopied ? 0.25 : 1,
                    filter: emailCopied ? 'blur(4px)' : 'blur(0px)',
                  }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    <CopySimple size={16} weight="fill" className="text-white" />
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                    <CopySimple size={16} weight="regular" className="text-white" />
                  </span>
                </motion.span>
                <motion.span
                  aria-hidden
                  animate={{
                    opacity: emailCopied ? 1 : 0,
                    scale: emailCopied ? 1 : 0.25,
                    filter: emailCopied ? 'blur(0px)' : 'blur(4px)',
                  }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <Check size={16} weight="bold" className="text-white" />
                </motion.span>
              </span>
            </button>
          </span>
        </p>
      </div>
    </div>
  </section>
)

export const LexFollowUpPage = () => {
  const [emailCopied, setEmailCopied] = useState(false)
  const [scheduleLinkHovered, setScheduleLinkHovered] = useState(false)
  const [avatarHovered, setAvatarHovered] = useState(false)
  const [avatarSwapped, setAvatarSwapped] = useState(false)
  const resetTimerRef = useRef<number | null>(null)
  const readAlsoCaseStudies = readAlsoSlugs
    .map((slug) => getAllCaseStudies().find((cs) => caseStudySlug(cs) === slug))
    .filter((cs): cs is ReturnType<typeof getAllCaseStudies>[number] => Boolean(cs))
  const readAlsoNotes = getAllNotes().slice(0, 2)

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current)
      }
    },
    [],
  )

  const handleCopyEmail = async () => {
    const ok = await copyContactEmail()
    if (!ok) {
      return
    }
    setEmailCopied(true)
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current)
    }
    resetTimerRef.current = window.setTimeout(() => {
      setEmailCopied(false)
      resetTimerRef.current = null
    }, 1800)
  }

  return (
    <>
      <SeoHead
        pathname={pathname}
        kind="private-follow-up"
        ogImagePath="/og/lex-follow-up.png"
        ogImageAlt="Lex and Gosha portraits for a FinchTrade post-call sales asset preview."
      />
      <div className="site-page mx-auto w-full max-w-[448px] px-4 pb-24 md:pt-28">
        <div className="space-y-14">
          <header className="space-y-6">
            <motion.button
              type="button"
              aria-label="Swap Lex portrait and FinchTrade logo"
              onClick={() => setAvatarSwapped((current) => !current)}
              onMouseEnter={() => setAvatarHovered(true)}
              onMouseLeave={() => setAvatarHovered(false)}
              onFocus={() => setAvatarHovered(true)}
              onBlur={() => setAvatarHovered(false)}
              whileTap={{ scale: 0.96 }}
              className="inline-flex h-[60px] w-10 cursor-pointer items-center justify-center self-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
            >
              <div className="relative h-[60px] w-10">
                <motion.img
                  src={lexPortrait}
                  alt="Lex"
                  width={40}
                  height={60}
                  animate={{
                    x: avatarSwapped ? 3 : 0,
                    y: avatarSwapped ? 3 : 0,
                    scale: avatarSwapped ? 0.94 : 1,
                    rotate: avatarSwapped ? (avatarHovered ? 6 : 2) : avatarHovered ? -6 : -1,
                  }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                  className="absolute inset-0 z-20 h-full w-full rounded-[6px] object-cover shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                  decoding="async"
                />
                <motion.span
                  aria-hidden
                  animate={{
                    x: avatarSwapped ? 0 : 3,
                    y: avatarSwapped ? 0 : 3,
                    scale: avatarSwapped ? 1 : 0.94,
                    rotate: avatarSwapped ? (avatarHovered ? -6 : -1) : avatarHovered ? 6 : 2,
                  }}
                  transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
                  className="absolute inset-0 flex h-full w-full items-center justify-center rounded-[6px] bg-black p-2 shadow-[0_7px_18px_rgba(0,0,0,0.14)]"
                  style={{ zIndex: avatarSwapped ? 20 : 10 }}
                >
                  <img
                    src={finchtradeLogo}
                    alt=""
                    width={24}
                    height={24}
                    className="size-6 object-contain"
                    decoding="async"
                  />
                </motion.span>
              </div>
            </motion.button>
            <div className="space-y-4">
              <h1 className="ui-heading-balance font-['Familjen_Grotesk'] text-[32px] font-normal leading-[1.02] tracking-[-0.32px] text-[var(--foreground)]">
                <img
                  src={wavingHand}
                  alt=""
                  width={32}
                  height={32}
                  className="rain-wave-icon mr-2 inline-block size-8"
                  aria-hidden="true"
                  decoding="async"
                />
                Lex! Thanks for the call. Here is the follow-up
              </h1>
              <p className="text-[15px] leading-6 tracking-[-0.01em] text-[var(--foreground-muted)] [text-wrap:pretty]">
                Lex, this page turns our call into a post-call sales asset for FinchTrade. The real
                question is:{' '}
                <strong className="font-medium text-[var(--foreground)]">
                  how do we make the next step feel obvious after the call?
                </strong>
              </p>
            </div>
            <p className="text-[15px] leading-6 tracking-[-0.01em] text-[var(--foreground-muted)] [text-wrap:pretty]">
              If you want to chat again, you can{' '}
              <a
                href={siteConfig.calendlyUrl}
                rel="noreferrer"
                target="_blank"
                onMouseEnter={() => setScheduleLinkHovered(true)}
                onMouseLeave={() => setScheduleLinkHovered(false)}
                onFocus={() => setScheduleLinkHovered(true)}
                onBlur={() => setScheduleLinkHovered(false)}
                aria-label="Book next call (opens in new tab)"
                className="inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
              >
                book next call
                <span className="relative inline-flex size-4 items-center justify-center">
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: scheduleLinkHovered ? 0 : 1,
                      scale: scheduleLinkHovered ? 0.25 : 1,
                      filter: scheduleLinkHovered ? 'blur(4px)' : 'blur(0px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <CalendarDots aria-hidden size={16} weight="regular" />
                  </motion.span>
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: scheduleLinkHovered ? 1 : 0,
                      scale: scheduleLinkHovered ? 1 : 0.25,
                      filter: scheduleLinkHovered ? 'blur(0px)' : 'blur(4px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <ArrowUpRight aria-hidden size={16} weight="regular" />
                  </motion.span>
                </span>
              </a>
              {' '}or here is my{' '}
              <button
                type="button"
                onClick={handleCopyEmail}
                aria-label={emailCopied ? 'Email copied' : 'Copy email address'}
                className="group inline-flex items-center gap-1 font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
              >
                email
                <span className="relative inline-flex size-5 items-center justify-center">
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: emailCopied ? 0 : 1,
                      scale: emailCopied ? 0.25 : 1,
                      filter: emailCopied ? 'blur(4px)' : 'blur(0px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                      <CopySimple size={16} weight="fill" />
                    </span>
                    <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                      <CopySimple size={16} weight="regular" />
                    </span>
                  </motion.span>
                  <motion.span
                    aria-hidden
                    animate={{
                      opacity: emailCopied ? 1 : 0,
                      scale: emailCopied ? 1 : 0.25,
                      filter: emailCopied ? 'blur(0px)' : 'blur(4px)',
                    }}
                    transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <Check size={16} weight="bold" />
                  </motion.span>
                </span>
              </button>
              .
            </p>
          </header>

          <Section
            eyebrow="What I heard"
            title={
              <>
                <img
                  src={finchtradeLogo}
                  alt=""
                  width={24}
                  height={24}
                  className="mr-1 inline-block size-6 -translate-y-0.5 rounded-[5px] bg-black p-1"
                  aria-hidden="true"
                  decoding="async"
                />
                FinchTrade already has the hard sales context after the meeting.
              </>
            }
          >
            <div className="space-y-4 text-[15px] leading-6 text-[var(--foreground-muted)] [text-wrap:pretty]">
              <p>
                The useful context appears during the call: payment rails, liquidity providers,
                current fees, treasury setup, KYB concerns, and the buyer’s real reason for asking
                about price. The gap is what happens{' '}
                <strong className="font-medium text-[var(--foreground)]">after a serious conversation</strong>.
                A tailored asset can turn that transcript, cost logic, and next-step plan into a{' '}
                <strong className="font-medium text-[var(--foreground)]">sales asset</strong> the
                buyer can open, share, and use internally.
              </p>
            </div>
          </Section>

          <section className="overflow-hidden rounded-[16px] bg-black text-white">
            <div className="space-y-4 px-4 pt-4 pb-0">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-white/45">
                  Solution
                </div>
                <h2 className="font-['Familjen_Grotesk'] text-[22px] font-normal leading-[1.12] tracking-[-0.2px] text-white [text-wrap:pretty]">
                  Turn one serious FinchTrade call into a deal page around the buyer’s economics.
                </h2>
              </div>
              <div className="rounded-[10px] border border-white/12 bg-white/7 text-[14px] leading-[21px] text-white/68">
                <div className="space-y-2 p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/42">to:</span>
                    <img
                      src={lexPortrait}
                      alt=""
                      width={20}
                      height={20}
                      className="size-5 rounded-full object-cover"
                      aria-hidden="true"
                      decoding="async"
                    />
                    <span>Lex · FinchTrade</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white/42">from:</span>
                    <img
                      src={goshaPortrait}
                      alt=""
                      width={20}
                      height={20}
                      className="size-5 rounded-full object-cover"
                      aria-hidden="true"
                      decoding="async"
                    />
                    <span>gknjazev@gmail.com</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div>
                    <span className="font-medium text-white/42">topic:</span>{' '}
                    <span>testing personalized follow-up assets</span>
                  </div>
                </div>
                <div className="border-t border-white/10 p-4 text-[15px] leading-6 [text-wrap:pretty]">
                  <p>
                    Lex, here is the asset I promised. I based it on our conversation about
                    post-call follow-up, custom pricing, KYB friction, and showing a buyer why their
                    current setup may be costing more than it should:{' '}
                    <a
                      href={pathname}
                      className="font-medium text-white underline decoration-white/45 underline-offset-3 transition-[text-decoration-color] duration-150 hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    >
                      open it
                    </a>
                    .
                  </p>
                </div>
              </div>
              <p className="text-[15px] font-medium leading-6 text-white [text-wrap:pretty]">
                Start building a customer-specific follow-up that makes the offer easier to believe: the current setup
                <Compass className={offerInlineIconClass} weight="regular" aria-hidden />, proofs
                <ShieldCheck className={offerInlineIconClass} weight="regular" aria-hidden />, next
                steps
                <FlagBanner className={offerInlineIconClass} weight="regular" aria-hidden />,
                savings case
                <Handshake className={offerInlineIconClass} weight="regular" aria-hidden />, answers
                <Question className={offerInlineIconClass} weight="regular" aria-hidden />, docs
                <FileDoc className={offerInlineIconClass} weight="regular" aria-hidden />, and the
                exact context from the call, all shaped around one buyer instead of a generic
                follow-up.
              </p>
            </div>
            <div className="relative h-[176px] overflow-hidden md:h-[220px]">
              <img
                src={wildflowerFieldPoster}
                alt=""
                width={960}
                height={540}
                className="h-full w-full object-cover md:hidden"
                aria-hidden="true"
                decoding="async"
              />
              <video
                src={offerVideoUrl}
                poster={wildflowerFieldPoster}
                className="hidden h-full w-full object-cover md:block"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black via-black/55 to-transparent" />
            </div>
          </section>

          <Section eyebrow="Benefits" title="Why this can work after a sales call.">
            <div className="grid grid-cols-2 gap-2">
              {benefits.map((benefit) => (
                <article key={benefit.title} className="rounded-[10px] border border-[var(--border)] bg-white p-4">
                  <benefit.Icon
                    aria-hidden
                    className="mb-3 size-8 text-[var(--foreground-subtle)]"
                    weight="regular"
                  />
                  <h3 className="font-['Familjen_Grotesk'] text-[18px] font-normal leading-6 tracking-[-0.16px] text-[var(--foreground)]">
                    {benefit.title}
                  </h3>
                  <p className="mt-4 text-[14px] leading-[22px] text-[var(--foreground-muted)] [text-wrap:pretty]">
                    {benefit.body}
                  </p>
                </article>
              ))}
            </div>
          </Section>

          <div className="py-2">
            <EditorialQuote
              body="The most important part is after the call: collect enough information, calculate how useful we actually are, and show why the client should work with us."
              name="Lex"
              role="FinchTrade"
              photoUrl={lexPortrait}
            />
          </div>

          <Section
            eyebrow="Pilot"
            title={
              <>
                <img
                  src={rocketIcon}
                  alt=""
                  width={24}
                  height={24}
                  className="rocket-shake-icon mr-2 inline-block size-6"
                  aria-hidden="true"
                  decoding="async"
                />
                Start with one internal sales asset, not a customer-facing follow-up.
              </>
            }
          >
            <div className="space-y-3">
              {pilotSteps.map((item, index) => (
                <div key={index} className="flex items-center gap-3 rounded-[10px] border border-white/10 bg-black p-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-[12px] font-semibold leading-none text-black">
                    {index + 1}
                  </span>
                  <p className="text-[14px] leading-[22px] text-white/72">{item}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Questions from the call.">
            <CaseStudyFaqAccordion items={faqItems} />
          </Section>

          <FollowUpPromptPromo onCopyEmail={handleCopyEmail} emailCopied={emailCopied} />

          <div className="-mx-4">
            <LazyMotion features={loadDomAnimation} strict>
              <Suspense fallback={null}>
                <HomeTestimonials />
              </Suspense>
            </LazyMotion>
          </div>

          <HomeAiCaseStudies
            title="Read also"
            items={readAlsoCaseStudies}
            notes={readAlsoNotes}
            showExploreMore={false}
            padded={false}
            variant="readAlso"
          />
        </div>
      </div>
    </>
  )
}
